import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import { Button, Drawer, Message, Modal, Tabs, Tag } from "@arco-design/web-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, Image as ImageIcon, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

import {
  resourcePointHeritageDescriptionLabels,
  resourcePointMediaSceneOptions,
} from "../config";
import { fetchResourcePointMedia, obsoleteResourcePointMedia } from "../repo";
import { resourcePointSpuQueryRoot } from "../runtime";
import {
  buildHeritagePptDownloadUrl,
  buildHeritagePptOpenUrl,
  groupHeritageMediaByScene,
  parseHeritageDescription,
  resourcePointMutationErrorMessage,
} from "../service";
import type { ResourcePointMediaItem, ResourcePointMediaScene, ResourcePointSpuItem } from "../types";
import { MarkdownContent } from "../../../shared/ui";

export type ResourcePointHeritageDetailMode = "detail" | "teaching" | "product";

type ResourcePointHeritageDetailDrawerProps = {
  spu: ResourcePointSpuItem | null;
  mode?: ResourcePointHeritageDetailMode;
  onClose: () => void;
  onUpload?: (
    spu: ResourcePointSpuItem,
    lockedScene: ResourcePointMediaScene,
    existingItem?: ResourcePointMediaItem,
  ) => void;
  canManage?: boolean;
  canDelete?: boolean;
};

const detailScenes: ResourcePointMediaScene[] = ["introduction", "ppt", "description"];

/** 获取素材场景的中文名称。 */
function sceneLabel(scene: ResourcePointMediaScene): string {
  return resourcePointMediaSceneOptions.find((item) => item.value === scene)?.label ?? scene;
}

/** 获取素材场景为空时的引导文案。 */
function emptyHint(scene: ResourcePointMediaScene): string {
  if (scene === "introduction") return "暂无传承人文案，可先填写。";
  if (scene === "ppt") return "暂无 PPT，可上传一个 .ppt/.pptx 文件。";
  if (scene === "description") return "暂无描述文案，可先上传。";
  if (scene === "teaching") return "暂无教学素材，可先上传。";
  return "暂无产品素材，可先上传。";
}

/** 获取素材场景当前状态对应的操作文案。 */
function actionLabel(scene: ResourcePointMediaScene, hasContent: boolean): string {
  if (scene === "introduction") return hasContent ? "编辑传承人" : "填写传承人";
  if (scene === "ppt") return hasContent ? "替换 PPT" : "上传 PPT";
  if (scene === "description") return hasContent ? "编辑描述" : "上传描述";
  return "上传素材";
}

/** 询问是否删除素材。 */
function confirmDeleteMedia(mediaName: string): Promise<boolean> {
  return new Promise((resolve) => {
    Modal.confirm({
      title: "删除素材",
      content: `确认删除「${mediaName}」吗？删除后将作废该素材，不再在列表中展示。`,
      okText: "确认删除",
      cancelText: "取消",
      okButtonProps: { status: "danger" },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/** 查找限制为单条内容的场景现有素材。 */
function existingItemForScene(
  scene: ResourcePointMediaScene,
  grouped: ReturnType<typeof groupHeritageMediaByScene>,
  introductionItem?: ResourcePointMediaItem,
  descriptionItem?: ResourcePointMediaItem,
): ResourcePointMediaItem | undefined {
  if (scene === "introduction") return introductionItem;
  if (scene === "description") return descriptionItem;
  if (scene === "ppt") return grouped.ppt[0];
  return undefined;
}

/** 非遗素材详情：列表计数进教学/产品；详情入口按 Tab 展示传承人 / PPT / 描述。 */
export function ResourcePointHeritageDetailDrawer({
  spu,
  mode = "detail",
  onClose,
  onUpload,
  canManage = false,
  canDelete = false,
}: ResourcePointHeritageDetailDrawerProps) {
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<ResourcePointMediaScene>("introduction");
  const enabled = Boolean(spu);
  const mediaQuery = useQuery({
    queryKey: ["resource-point-management", "media", spu?.resourcePointSpuId],
    queryFn: () => fetchResourcePointMedia(spu!.resourcePointSpuId),
    enabled,
    retry: false,
    placeholderData: [],
  });
  const items = mediaQuery.data ?? [];
  const grouped = groupHeritageMediaByScene(items);
  const isGalleryMode = mode === "teaching" || mode === "product";
  const activeScene: ResourcePointMediaScene = isGalleryMode ? mode : detailTab;
  const introductionItem = grouped.introduction[0];
  const descriptionItem = grouped.description[0];
  const description = parseHeritageDescription(descriptionItem?.contentText);
  const activeItems = grouped[activeScene];
  const activeHasContent = activeScene === "introduction"
    ? Boolean(introductionItem?.contentText?.trim())
    : activeScene === "description"
      ? Boolean(descriptionItem)
      : activeItems.length > 0;
  const showEmpty = !activeHasContent;
  const titleSuffix = mode === "teaching"
    ? "教学素材"
    : mode === "product"
      ? "产品素材"
      : "非遗详情";

  useEffect(() => {
    if (!spu) {
      setDetailTab("introduction");
    }
  }, [spu]);

  /** 作废指定素材并刷新详情与列表缓存。 */
  async function handleDelete(item: ResourcePointMediaItem) {
    if (!spu || !canDelete || deletingId) return;
    const confirmed = await confirmDeleteMedia(item.mediaName || "未命名素材");
    if (!confirmed) return;
    setDeletingId(item.resourcePointMediaId);
    try {
      await obsoleteResourcePointMedia(spu.resourcePointSpuId, item.resourcePointMediaId);
      Message.success("素材已删除");
      await queryClient.invalidateQueries({
        queryKey: ["resource-point-management", "media", spu.resourcePointSpuId],
      });
      await queryClient.invalidateQueries({ queryKey: resourcePointSpuQueryRoot });
    } catch (error) {
      Message.error(resourcePointMutationErrorMessage(error));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Drawer
      className="resource-point-heritage-drawer"
      title={spu ? `${spu.pointName} · ${titleSuffix}` : titleSuffix}
      visible={Boolean(spu)}
      width={isGalleryMode ? 560 : 720}
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={canManage && spu ? (
        <Button
          type="primary"
          long
          onClick={() => {
            const existingItem = activeHasContent
              ? existingItemForScene(activeScene, grouped, introductionItem, descriptionItem)
              : undefined;
            onUpload?.(spu, activeScene, existingItem);
          }}
        >
          {actionLabel(activeScene, activeHasContent)}
        </Button>
      ) : null}
      onCancel={onClose}
    >
      <div className="resource-point-heritage-detail">
        {mediaQuery.isError ? (
          <div className="resource-point-error" role="alert">非遗素材加载失败，请稍后重试。</div>
        ) : null}

        {mode === "detail" ? (
          <Tabs
            className="resource-point-heritage-tabs"
            activeTab={detailTab}
            onChange={(key) => setDetailTab(key as ResourcePointMediaScene)}
            type="line"
            size="large"
          >
            {detailScenes.map((scene) => (
              <Tabs.TabPane key={scene} title={sceneLabel(scene)}>
                {scene === "introduction" && introductionItem?.contentText?.trim() ? (
                  <section className="resource-point-heritage-description" aria-label="传承人文案">
                    <header className="resource-point-heritage-section__header">
                      <h3>传承人</h3>
                      <div className="resource-point-heritage-section__actions">
                        {canDelete ? (
                          <Button
                            type="text"
                            size="mini"
                            status="danger"
                            loading={deletingId === introductionItem.resourcePointMediaId}
                            aria-label={`删除 ${introductionItem.mediaName || "传承人"}`}
                            icon={<Trash2 size={14} />}
                            onClick={() => void handleDelete(introductionItem)}
                          />
                        ) : null}
                      </div>
                    </header>
                    <article className="resource-point-heritage-description__block resource-point-heritage-description__block--hero">
                      <MarkdownContent
                        className="resource-point-heritage-markdown"
                        content={introductionItem.contentText ?? ""}
                      />
                    </article>
                  </section>
                ) : null}

                {scene === "description" && descriptionItem ? (
                  <section className="resource-point-heritage-description" aria-label="描述文案">
                    <header className="resource-point-heritage-section__header">
                      <h3>描述</h3>
                      <div className="resource-point-heritage-section__actions">
                        <Tag color="arcoblue">介绍文案</Tag>
                        {canDelete ? (
                          <Button
                            type="text"
                            size="mini"
                            status="danger"
                            loading={deletingId === descriptionItem.resourcePointMediaId}
                            aria-label={`删除 ${descriptionItem.mediaName || "描述文案"}`}
                            icon={<Trash2 size={14} />}
                            onClick={() => void handleDelete(descriptionItem)}
                          />
                        ) : null}
                      </div>
                    </header>
                    {(Object.keys(resourcePointHeritageDescriptionLabels) as Array<
                      keyof typeof resourcePointHeritageDescriptionLabels
                    >).map((key) => {
                      const text = description[key].trim();
                      if (!text) return null;
                      return (
                        <article key={key} className="resource-point-heritage-description__block">
                          <h4>{resourcePointHeritageDescriptionLabels[key]}</h4>
                          <p>{text}</p>
                        </article>
                      );
                    })}
                  </section>
                ) : null}

                {scene === "ppt" ? (
                  <HeritageMediaSection
                    scene={scene}
                    items={grouped.ppt}
                    canDelete={canDelete}
                    deletingId={deletingId}
                    onDelete={handleDelete}
                  />
                ) : null}

                {(
                  scene === "introduction"
                    ? !introductionItem?.contentText?.trim()
                    : scene === "description"
                      ? !descriptionItem
                      : grouped.ppt.length === 0
                ) ? (
                  <div className="resource-point-state">{emptyHint(scene)}</div>
                ) : null}
              </Tabs.TabPane>
            ))}
          </Tabs>
        ) : (
          <>
            <HeritageMediaSection
              scene={activeScene}
              items={activeItems}
              canDelete={canDelete}
              deletingId={deletingId}
              onDelete={handleDelete}
            />
            {showEmpty ? (
              <div className="resource-point-state">{emptyHint(activeScene)}</div>
            ) : null}
          </>
        )}
      </div>
    </Drawer>
  );
}

/** 渲染指定场景的素材分区。 */
function HeritageMediaSection({
  scene,
  items,
  canDelete,
  deletingId,
  onDelete,
}: {
  scene: ResourcePointMediaScene;
  items: ResourcePointMediaItem[];
  canDelete: boolean;
  deletingId: string | null;
  onDelete: (item: ResourcePointMediaItem) => void;
}) {
  if (items.length === 0) return null;
  const label = sceneLabel(scene);
  return (
    <section className="resource-point-heritage-section" aria-label={`${label}素材`}>
      <header className="resource-point-heritage-section__header">
        <h3>{label}</h3>
        <Tag>{items.length}</Tag>
      </header>
      <div className="resource-point-heritage-grid">
        {items.map((item) => (
          <HeritageMediaCard
            key={item.resourcePointMediaId}
            item={item}
            canDelete={canDelete}
            deleting={deletingId === item.resourcePointMediaId}
            onDelete={onDelete}
          />
        ))}
      </div>
    </section>
  );
}

/** 渲染单个非遗素材卡片。 */
function HeritageMediaCard({
  item,
  canDelete,
  deleting,
  onDelete,
}: {
  item: ResourcePointMediaItem;
  canDelete: boolean;
  deleting: boolean;
  onDelete: (item: ResourcePointMediaItem) => void;
}) {
  const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(item.fileUrl)
    || item.fileExt?.match(/^(png|jpe?g|gif|webp|bmp)$/i);
  const isPpt = item.mediaScene === "ppt"
    || /\.(ppt|pptx)$/i.test(item.fileUrl)
    || item.fileExt?.match(/^(ppt|pptx)$/i);
  const pptDownloadUrl = isPpt
    ? buildHeritagePptDownloadUrl(item.fileUrl, item.mediaName || "PPT", item.fileExt)
    : item.fileUrl;
  const pptOpenUrl = isPpt ? buildHeritagePptOpenUrl(item.fileUrl) : item.fileUrl;

  if (isPpt) {
    const displayName = item.mediaName?.trim() || "演示文稿";
    const extLabel = (item.fileExt || "pptx").replace(/^\./, "").toUpperCase();
    return (
      <div className="resource-point-heritage-card resource-point-heritage-card--ppt">
        {canDelete ? (
          <Button
            className="resource-point-heritage-card__delete"
            type="text"
            size="mini"
            status="danger"
            loading={deleting}
            aria-label={`删除 ${displayName}`}
            icon={<Trash2 size={14} />}
            onClick={() => void onDelete(item)}
          />
        ) : null}
        <a
          className="resource-point-heritage-card__link"
          href={resolveHttpResourceUrl(pptOpenUrl)}
          target="_blank"
          rel="noreferrer"
          aria-label={`打开 ${displayName}`}
        >
          <div className="resource-point-heritage-card__preview resource-point-heritage-card__preview--ppt">
            <span className="resource-point-heritage-card__slide" aria-hidden>
              <span className="resource-point-heritage-card__slide-accent" />
              <span className="resource-point-heritage-card__slide-lines">
                <i />
                <i />
                <i />
              </span>
            </span>
          </div>
          <div className="resource-point-heritage-card__meta">
            <strong>{displayName}</strong>
            <span>{extLabel} 文件</span>
          </div>
        </a>
        <div className="resource-point-heritage-card__actions">
          <a
            className="resource-point-heritage-card__action"
            href={resolveHttpResourceUrl(pptOpenUrl)}
            target="_blank"
            rel="noreferrer"
          >
            <Eye size={14} />
            打开
          </a>
          <a
            className="resource-point-heritage-card__action"
            href={resolveHttpResourceUrl(pptDownloadUrl)}
            target="_blank"
            rel="noreferrer"
          >
            <Download size={14} />
            下载
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="resource-point-heritage-card">
      {canDelete ? (
        <Button
          className="resource-point-heritage-card__delete"
          type="text"
          size="mini"
          status="danger"
          loading={deleting}
          aria-label={`删除 ${item.mediaName}`}
          icon={<Trash2 size={14} />}
          onClick={() => void onDelete(item)}
        />
      ) : null}
      <a
        className="resource-point-heritage-card__link"
        href={resolveHttpResourceUrl(item.fileUrl)}
        target="_blank"
        rel="noreferrer"
        aria-label={item.mediaName}
      >
        <div className="resource-point-heritage-card__preview">
          {isImage ? (
            <img src={resolveHttpResourceUrl(item.fileUrl)} alt="" loading="lazy" />
          ) : (
            <span className="resource-point-heritage-card__icon">
              <ImageIcon size={28} />
            </span>
          )}
        </div>
        <div className="resource-point-heritage-card__meta">
          <strong>{item.mediaName}</strong>
          {item.contentText ? <span>{item.contentText}</span> : null}
        </div>
      </a>
    </div>
  );
}
